import { Box, Button, Grid, HStack, Icon, Link, Text } from '@chakra-ui/react'
import { EmptyMessage } from 'components/ui/emptyMessage'
import { Loading } from 'components/ui/loading'
import { NextLink } from 'components/wrapper/nextLink'
import { useModalDispatcher } from 'context/ui/modal'
import { TopMySpotItemFragment } from 'generated/graphql'
import { formatDate } from 'helper/dateFormat'
import { FC } from 'react'
import { MdEdit, MdLocationPin } from 'react-icons/md'
import { Routes } from 'routes'
import { buildMapSpotFallbackSearchCondition } from './buildMapSpotFallbackSearchCondition'
import { buildEditMySpotsModal } from './editMySpotsModal'
import { TopSectionHeader } from './topSectionHeader'
import { TopSectionLoadError } from './topSectionLoadError'

type Props = {
  mySpots?: TopMySpotItemFragment[]
  loading: boolean
  error: boolean
  refetch: () => Promise<unknown>
}

export const TopMySpotSection: FC<Props> = ({ mySpots, loading, error, refetch }) => {
  const { showModal } = useModalDispatcher()

  return (
    <Box as="section">
      <TopSectionHeader
        title="マイスポット"
        description="旅行先のよく使う場所を保存します"
        action={
          <Button
            leftIcon={<Icon as={MdEdit} boxSize="20px" />}
            variant="outline"
            borderColor="primary"
            color="primary"
            isDisabled={!mySpots}
            onClick={() => mySpots && showModal(buildEditMySpotsModal({ mySpots, refetch }))}
          >
            編集する
          </Button>
        }
      />

      <Box mt="24px">
        {!mySpots && loading ? (
          <Loading />
        ) : !mySpots && error ? (
          <TopSectionLoadError resourceName="マイスポット" onRetry={refetch} />
        ) : !mySpots || mySpots.length === 0 ? (
          <EmptyMessage>登録されているマイスポットはありません</EmptyMessage>
        ) : (
          <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="16px">
            {mySpots.map(spot => {
              const href = { pathname: Routes.searchHotelsMap(), query: buildMapSpotFallbackSearchCondition(spot) }
              return (
                <Box key={spot.id} p="24px" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="8px" boxShadow="sm">
                  <NextLink href={href} passHref legacyBehavior>
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      color="gray.800"
                      fontSize="18px"
                      fontWeight="bold"
                      wordBreak="break-word"
                    >
                      {spot.title}
                    </Link>
                  </NextLink>
                  <HStack mt="16px" spacing="24px" color="gray.600" fontSize="sm">
                    <Text>登録日 {formatDate(new Date(spot.createdAt), 'yyyy/MM/dd')}</Text>
                    <Text wordBreak="break-word">登録者 {spot.createdByName}</Text>
                  </HStack>
                  <NextLink href={href} passHref legacyBehavior>
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      display="inline-flex"
                      alignItems="center"
                      gap="6px"
                      mt="16px"
                      color="primary"
                      fontWeight="medium"
                    >
                      <Icon as={MdLocationPin} boxSize="20px" />
                      地図を見る
                    </Link>
                  </NextLink>
                </Box>
              )
            })}
          </Grid>
        )}
      </Box>
    </Box>
  )
}
